/**
 * PORTED — RO-3-00c / #44470 + RO-3-03 / #44296 (Work domain bodies).
 * Origin: `excellent/app/_modes/domain-views.tsx`.
 *
 * Domain mount-adapters. Server data access is gone — each pure-data view
 * fetches through a `modes.*` READ contract.
 *
 * RO-3-03 filled the Work pending shells:
 *   IssuesBoardView / ReviewInboxView → IssuesPage (modes.issuesBoard)
 *   WorkPlannerView                   → PlannerPage (modes.planner)
 *   WorkInboxView                     → WorkSurface (modes.workInbox)
 *   WorkObjectivesView                → GoalsView (agents shared)
 *   WorkChatView                      → WorkChatStageLauncher
 *
 * Cause-A rewrites delivered here:
 *   WorkProjectsView  → modes.workProjects
 *   WorkInboxView     → modes.workInbox
 *   FinanceLedgerView → modes.financeLedger
 *   object-kind rows  → modes.objectKinds
 *   section presence  → modes.sectionExists
 *
 * The large CompanyStream / TeamDirectory / Roadmap / Quality / ProjectLinks
 * surfaces keep their structure but load via `modes.data` + client UI where a
 * dedicated contract is not yet justified; they degrade to EmptyState when
 * empty rather than re-implementing the full 1,148-LOC body in this commit.
 * Section items can deepen them without re-doing the Mode Shell spine.
 */

import {
  EmptyState,
  ProjectsBoard,
  StatStrip,
  type StatStripItem,
  Card,
  CardContent,
  SectionHeading,
  BoundedScroll,
} from "@excellent/ui";
import { Boxes, Wallet, Network, Milestone } from "@excellent/ui/icons";
import { MembersPanel } from "../settings/team-panels";
import { apiCall, useApi } from "@excellent/ui/api";
import { modes } from "@excellent/ui/api/contracts/modes";
import { projectActions } from "@excellent/ui/api/contracts/project-actions";
import { ObjectsIndexClient, type ObjectKindRow } from "../database/objects-index-client";
import { FinanceScopeActions } from "../database/finance-scope-actions";
import { FINANCE_KINDS } from "../database/scope-kinds";
import { SectionColdStartDegrade } from "./section-cold-start";
import { IssuesPage } from "../work/issues-page";
import { PlannerPage } from "../work/planner-page";
import { WorkSurface } from "../work/work-surface";
import { WorkChatStageLauncher } from "../work/work-chat-stage-launcher";
import { GoalsView } from "../agents/views";
import type { WorkItem } from "@/work/types";

interface ScopedProps {
  projectSlug: string | null;
  searchParams: Record<string, string>;
  headerExternal?: boolean;
  sectionSlug?: string;
}

function PendingBody({ title, note }: { title: string; note: string }) {
  return (
    <EmptyState
      variant="inline"
      icon={<Boxes className="size-5" />}
      title={title}
      description={note}
    />
  );
}

function scopeProjectParam(
  searchParams: Record<string, string>,
  projectSlug: string | null,
): Record<string, string> {
  if (!projectSlug) return searchParams;
  if (searchParams.project) return searchParams;
  return { ...searchParams, project: projectSlug };
}

/** Work ▸ Reviews — mounts Issues forced to its review view. */
export function ReviewInboxView({
  projectSlug,
  searchParams,
  headerExternal,
}: ScopedProps) {
  const sp = scopeProjectParam(searchParams ?? {}, projectSlug);
  return (
    <IssuesPage
      searchParams={{ ...sp, view: "review" }}
      controlsHoisted
      headerExternal={headerExternal}
    />
  );
}

/** Work ▸ Issues list/board. */
export function IssuesBoardView({
  projectSlug,
  searchParams,
  headerExternal,
}: ScopedProps) {
  const sp = scopeProjectParam(searchParams ?? {}, projectSlug);
  const withDefault =
    "status" in sp || "section" in sp
      ? sp
      : { ...sp, status: "in_progress,review,todo,backlog", range: "all" };
  return (
    <IssuesPage
      searchParams={withDefault}
      controlsHoisted
      headerExternal={headerExternal}
    />
  );
}

/** Work ▸ Plan — strategic + agentic goals as one GoalsView. */
export function WorkObjectivesView() {
  return (
    <div className="flex w-full flex-col px-6 pb-8">
      <GoalsView basePath="/" />
    </div>
  );
}

export function WorkProjectsView() {
  const { data, isLoading, mutate } = useApi(modes.workProjects, {});
  if (isLoading && !data) return null;
  const groups = (data?.groups ?? []) as Parameters<typeof ProjectsBoard>[0]["groups"];
  return (
    <ProjectsBoard
      groups={groups}
      onCreateProject={async (name) => {
        const res = await apiCall(projectActions.createForBoard, { body: { name } });
        if (res.ok) void mutate();
        return res;
      }}
    />
  );
}

/** Issues ▸ Calendar. */
export function WorkPlannerView({ searchParams }: ScopedProps) {
  return (
    <BoundedScroll mode="scroll">
      <PlannerPage searchParams={searchParams ?? {}} />
    </BoundedScroll>
  );
}

/** Work ▸ Chat — summons the shared Stage (same bus as Play). */
export function WorkChatView() {
  return <WorkChatStageLauncher />;
}

/** Work ▸ Inbox — full WorkSurface over modes.workInbox. */
export function WorkInboxView() {
  const { data, isLoading } = useApi(modes.workInbox, {});
  if (isLoading && !data) return null;
  const items = (data?.items ?? []) as WorkItem[];
  return <WorkSurface items={items} />;
}

export function MarketingAnalyticsView({ projectSlug }: ScopedProps) {
  if (!projectSlug) {
    return (
      <SectionColdStartDegrade
        slug="marketing"
        label="Web Analytics"
        hint="Pick a project — marketing analytics sources are configured per project."
      />
    );
  }
  return (
    <PendingBody
      title="Web Analytics"
      note="Per-project analytics page mounts with the Customers/Marketing section port."
    />
  );
}

export function MarketingSeoView({ projectSlug }: ScopedProps) {
  if (!projectSlug) {
    return (
      <SectionColdStartDegrade
        slug="marketing"
        label="SEO workbench"
        hint="Pick a project — SEO targets and scans are configured per project."
      />
    );
  }
  return (
    <PendingBody
      title="SEO workbench"
      note="Per-project SEO page mounts with the Customers/Marketing section port."
    />
  );
}

export function DeliveryPerformanceView({ projectSlug }: ScopedProps) {
  if (!projectSlug) {
    return (
      <SectionColdStartDegrade
        label="Performance"
        hint="Performance baselines are captured per project."
      />
    );
  }
  return (
    <PendingBody
      title="Performance"
      note="Per-project perf audit mounts with the Product section port."
    />
  );
}

export function DeliveryPrdView({ projectSlug }: ScopedProps) {
  if (!projectSlug) {
    return (
      <SectionColdStartDegrade
        label="PRD"
        hint="PRDs are authored per project."
      />
    );
  }
  return (
    <PendingBody title="PRD" note="Per-project PRD page mounts with the Product section port." />
  );
}

export function ProjectLinksView({ projectSlug }: ScopedProps) {
  if (!projectSlug) {
    return (
      <SectionColdStartDegrade
        label="Project links"
        hint="Open a project to see its product umbrella and child codebases."
      />
    );
  }
  return (
    <PendingBody
      title="Project links"
      note="Workspace graph projection mounts fully when Product section deepens this adapter."
    />
  );
}

export function CompanyStreamView() {
  const { data } = useApi(modes.data, { query: { mode: "operations" } });
  return (
    <Card>
      <CardContent className="flex flex-col gap-3 py-6">
        <SectionHeading title="Company" description="People · hiring · money motion" />
        <p className="type-body text-muted-foreground">
          {data?.total ?? 0} operations commitments in the index. The full Company
          stream board deepens with the Operations section port.
        </p>
      </CardContent>
    </Card>
  );
}

export function TeamDirectoryView() {
  return <MembersPanel />;
}

export function FinanceLedgerView() {
  const { data, isLoading } = useApi(modes.financeLedger, {});
  if (isLoading && !data) return null;
  const stats = data?.stats;
  const kpis: StatStripItem[] = [
    { title: "Money accounts", value: String(stats?.accounts ?? 0) },
    { title: "Linked counterparties", value: String(stats?.counterparties ?? 0) },
    {
      title: "Transactions",
      value: String((data?.transactions as unknown[] | undefined)?.length ?? 0),
    },
  ];
  const rawRows = (data?.objectRows ?? []) as ObjectKindRow[];
  return (
    <div className="flex flex-col gap-4 px-6 pb-8">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="type-title font-medium">Finance</h2>
          <p className="type-body text-muted-foreground">
            Every transaction pulled in from your accounting and payments tools, in one place.
          </p>
        </div>
        <FinanceScopeActions rawRows={rawRows} />
      </div>
      <StatStrip items={kpis} variant="standalone" />
      {(data?.transactions as unknown[] | undefined)?.length ? (
        <p className="type-body text-muted-foreground">
          {(data!.transactions as unknown[]).length} transaction
          {(data!.transactions as unknown[]).length === 1 ? "" : "s"}
          {data?.capped ? " (capped)" : ""} loaded via modes.financeLedger. The full
          TransactionsBrowser mounts with the Operations section port.
        </p>
      ) : (
        <EmptyState
          variant="inline"
          icon={<Wallet className="size-5" />}
          title="No transactions yet"
          description="Connect accounting tools to populate the ledger."
        />
      )}
      {rawRows.length > 0 ? (
        <ObjectsIndexClient rows={rawRows} chromeless />
      ) : null}
    </div>
  );
}

export function RoadmapView() {
  return (
    <EmptyState
      variant="inline"
      icon={<Milestone className="size-5" />}
      title="Roadmap"
      description="Initiatives and themes over agentic goals. Full table mounts with the Product section port; data plane is modes.data."
    />
  );
}

export function QualityView() {
  return (
    <EmptyState
      variant="inline"
      icon={<Network className="size-5" />}
      title="Quality"
      description="Section-audit findings for development + design. Mounts with RO-3-00f (section-audit-panel)."
    />
  );
}

export function CustomDomainView({ sectionSlug }: { sectionSlug?: string }) {
  return (
    <EmptyState
      variant="inline"
      icon={<Boxes className="size-5" />}
      title={sectionSlug ? `Custom domain · ${sectionSlug}` : "Custom domain"}
      description="User/agent-authored domains render from their pinned saved views. Full CustomDomainPanel deepens with the Database/modes section port."
    />
  );
}

/** Re-export for sales chrome / finance object overlays. */
export function useObjectRowsForKinds(kinds: readonly string[]) {
  const { data } = useApi(modes.objectKinds, {
    query: { kinds: kinds.join(",") },
  });
  return (data?.rows ?? []) as ObjectKindRow[];
}

export { FINANCE_KINDS };
